/**
 * useAdminResourceTags Hooks (T063 - US5)
 * React Query hooks for resource tags CRUD with optimistic updates
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { adminResourceTagsApi } from '../services/adminResourceTagsApi';
import type {
  AdminResourceTag,
  CreateResourceTagPayload,
  UpdateResourceTagPayload,
} from '../types/admin-resource.type';

type ListParams = {
  search?: string;
  isActive?: boolean;
};

export const resourceTagKeys = {
  all: ['admin', 'resource-tags'] as const,
  lists: () => [...resourceTagKeys.all, 'list'] as const,
  list: (params?: ListParams) => [...resourceTagKeys.lists(), params] as const,
};

export function useResourceTags(
  params?: ListParams,
): UseQueryResult<AdminResourceTag[], Error> {
  return useQuery({
    queryKey: resourceTagKeys.list(params),
    queryFn: () => adminResourceTagsApi.listResourceTags(params),
    staleTime: 300000,
  });
}

export function useCreateResourceTag(): UseMutationResult<
  AdminResourceTag,
  Error,
  CreateResourceTagPayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminResourceTagsApi.createResourceTag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.lists() });
    },
  });
}

export function useUpdateResourceTag(): UseMutationResult<
  AdminResourceTag,
  Error,
  UpdateResourceTagPayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      adminResourceTagsApi.updateResourceTag(id, payload),
    onMutate: async (updated) => {
      await queryClient.cancelQueries({ queryKey: resourceTagKeys.lists() });
      const queries = queryClient.getQueriesData<AdminResourceTag[]>({
        queryKey: resourceTagKeys.lists(),
      });
      const snapshots = queries.map(([key, data]) => [key, data] as const);

      queryClient.setQueriesData<AdminResourceTag[]>(
        { queryKey: resourceTagKeys.lists() },
        (old) =>
          old?.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)),
      );

      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      context?.snapshots?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.lists() });
    },
  });
}

export function useDeleteResourceTag(): UseMutationResult<
  AdminResourceTag,
  Error,
  { id: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }) => adminResourceTagsApi.deactivateResourceTag(id),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: resourceTagKeys.lists() });
      const queries = queryClient.getQueriesData<AdminResourceTag[]>({
        queryKey: resourceTagKeys.lists(),
      });
      const snapshots = queries.map(([key, data]) => [key, data] as const);

      queryClient.setQueriesData<AdminResourceTag[]>(
        { queryKey: resourceTagKeys.lists() },
        (old) =>
          old?.map((t) =>
            t.id === id ? { ...t, isActive: !t.isActive } : t,
          ),
      );

      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      context?.snapshots?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.lists() });
    },
  });
}
